import React, { useEffect, useMemo } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Button,
  Alert,
  Row,
  Col,
  Tag,
  Typography,
  TimePicker,
} from "antd";
import { InfoCircleOutlined, SaveOutlined, WarningOutlined } from "@ant-design/icons";
import { CorralDefinition, CreateCorralPayload } from "../types/corral.types";
import { BibRangeInfo } from "../hooks/useCorral";

const { Text } = Typography;

interface CreateCorralModalProps {
  open: boolean;
  onClose: () => void;
  eventId?: string;
  distance: string;
  existingCorrals: CorralDefinition[];
  bibRangesForDistance?: BibRangeInfo[];
  onSuccess: () => void;
  onSubmit: (payload: CreateCorralPayload) => Promise<boolean>;
}

const CreateCorralModal: React.FC<CreateCorralModalProps> = ({
  open,
  onClose,
  eventId,
  distance,
  existingCorrals,
  bibRangesForDistance = [],
  onSuccess,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = React.useState(false);

  // Merged allowed BIB range from all ticket ranges for this distance
  const allowedRange = useMemo(() => {
    if (bibRangesForDistance.length === 0) return null;
    return {
      start: Math.min(...bibRangesForDistance.map((r) => r.start)),
      end: Math.max(...bibRangesForDistance.map((r) => r.end)),
    };
  }, [bibRangesForDistance]);

  // Gaps available between existing corrals (free BIB slots)
  const usedRanges = useMemo(
    () =>
      [...existingCorrals]
        .sort((a, b) => a.bib_start - b.bib_start)
        .map((c) => ({ start: c.bib_start, end: c.bib_end, name: c.name })),
    [existingCorrals],
  );

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ distance });
    }
  }, [open, distance, form]);

  const checkOverlap = (start: number, end: number, excludeId?: string): CorralDefinition | null => {
    return (
      existingCorrals.find((c) => {
        if (excludeId && c.id === excludeId) return false;
        return start <= c.bib_end && end >= c.bib_start;
      }) ?? null
    );
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!eventId) return;

      const { bib_start, bib_end } = values;

      // 1. Overlap with existing corrals
      const conflicting = checkOverlap(bib_start, bib_end);
      if (conflicting) {
        form.setFields([
          {
            name: "bib_end",
            errors: [
              `Overlaps with corral "${conflicting.name}" (BIB ${conflicting.bib_start}–${conflicting.bib_end})`,
            ],
          },
        ]);
        return;
      }

      // 2. Must be within the defined BIB range for this distance
      if (allowedRange) {
        if (bib_start < allowedRange.start || bib_end > allowedRange.end) {
          const msg = `Must be within the defined BIB range ${allowedRange.start}–${allowedRange.end} for ${distance}.`;
          form.setFields([
            { name: "bib_start", errors: bib_start < allowedRange.start ? [msg] : [] },
            { name: "bib_end", errors: bib_end > allowedRange.end ? [msg] : [] },
          ]);
          return;
        }
      }

      setSaving(true);
      const ok = await onSubmit({
        event_id: eventId,
        distance: values.distance,
        name: values.name.trim().toUpperCase(),
        bib_start,
        bib_end,
        max_capacity: values.capacity || undefined,
        start_time: values.start_time
          ? values.start_time.format("HH:mm")
          : undefined,
      });

      if (ok) {
        form.resetFields();
        onSuccess();
        onClose();
      }
    } catch (err: any) {
      if (err?.errorFields) return;
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Create New Corral"
      open={open}
      onCancel={handleClose}
      destroyOnClose
      width={540}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
        >
          Create Corral
        </Button>,
      ]}
    >
      {/* Allowed BIB range hint */}
      {allowedRange ? (
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
          message={
            <span>
              Allowed BIB range for <strong>{distance}</strong>:{" "}
              <Text strong style={{ color: "#1d4ed8" }}>
                {allowedRange.start}–{allowedRange.end}
              </Text>
            </span>
          }
          description={
            usedRanges.length > 0 ? (
              <div style={{ marginTop: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Already used by corrals:{" "}
                </Text>
                {usedRanges.map((r) => (
                  <Tag key={r.name} color="orange" style={{ fontSize: 11 }}>
                    {r.name}: {r.start}–{r.end}
                  </Tag>
                ))}
              </div>
            ) : undefined
          }
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="No BIB range defined for this distance yet."
          description="Go to BIB Assignment and define a range first, then create corrals."
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Corral Name"
              rules={[
                { required: true, message: "Required" },
                { max: 10, message: "Max 10 characters" },
                {
                  validator: (_, val) => {
                    if (!val) return Promise.resolve();
                    const dup = existingCorrals.find(
                      (c) => c.name.toUpperCase() === val.trim().toUpperCase(),
                    );
                    return dup
                      ? Promise.reject(new Error(`Corral "${val.trim().toUpperCase()}" already exists`))
                      : Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder="e.g. A, B, Elite"
                maxLength={10}
                style={{ textTransform: "uppercase" }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="distance" label="Distance">
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="bib_start"
              label="BIB Start"
              rules={[
                { required: true, message: "Required" },
                {
                  validator: (_, val) => {
                    if (!val) return Promise.resolve();
                    if (allowedRange && val < allowedRange.start) {
                      return Promise.reject(
                        new Error(`Min allowed: ${allowedRange.start}`),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                min={allowedRange?.start ?? 1}
                max={allowedRange?.end}
                placeholder={allowedRange ? `${allowedRange.start}` : "e.g. 1"}
                className="w-full"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="bib_end"
              label="BIB End"
              dependencies={["bib_start"]}
              rules={[
                { required: true, message: "Required" },
                ({ getFieldValue }) => ({
                  validator(_, val) {
                    const start = getFieldValue("bib_start");
                    if (!val) return Promise.resolve();
                    if (val <= start)
                      return Promise.reject(new Error("Must be greater than BIB Start"));
                    if (allowedRange && val > allowedRange.end)
                      return Promise.reject(new Error(`Max allowed: ${allowedRange.end}`));

                    // Real-time overlap check
                    const conflict = existingCorrals.find(
                      (c) => start <= c.bib_end && val >= c.bib_start,
                    );
                    if (conflict)
                      return Promise.reject(
                        new Error(
                          `Overlaps with corral "${conflict.name}" (${conflict.bib_start}–${conflict.bib_end})`,
                        ),
                      );

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber
                min={allowedRange?.start ?? 1}
                max={allowedRange?.end}
                placeholder={allowedRange ? `${allowedRange.end}` : "e.g. 100"}
                className="w-full"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="capacity" label="Capacity (optional)">
              <InputNumber
                min={1}
                placeholder="Unlimited"
                className="w-full"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="start_time" label="Start Time (optional)">
              <TimePicker
                format="HH:mm"
                placeholder="e.g. 06:30"
                style={{ width: "100%" }}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CreateCorralModal;
